import { HTMLAttributes, useState, useCallback, useEffect } from 'react'
import Image from 'next/image'

import cl from 'classnames'

import { ArticlePreview } from '@components/article-preview'
import { Divider } from '@components/divider'
import {
  EditorialNavBar,
  EditorialNavBarItem
} from '@components/editorial-nav-bar'
import { SidebarArticlesPreview } from '@components/sidebar-articles-preview'

import styles from './styles.module.scss'
import { api } from '@services/api'
import { Highlight } from '@services/api/highlights'

import { Article } from 'types/article'
import { UseMoreReadArticles } from '@hooks/use-more-read-articles'

interface EditorialSummaryProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  editorialNavBarItems: EditorialNavBarItem[]
  highlights: Highlight[]
  hideNavBar?: boolean
}

export const EditorialSummary = ({
  editorialNavBarItems,
  highlights,
  className,
  hideNavBar,
  ...rest
}: EditorialSummaryProps): JSX.Element => {
  const [selectedEditorialNavBarItem, setSelectedEditorialNavBarItem] =
    useState<EditorialNavBarItem>(() => {
      const [editorialNavBarItem] = editorialNavBarItems
      return editorialNavBarItem
    })

  const [highlightsList, setHighlights] = useState<Highlight[]>(highlights)

  const [first, second, third, fourth] = highlightsList || []

  const [articles, setArticles] = useState<Article[]>([])

  const [loadingHighlights, setLoadingHighlights] = useState(false)
  const [loadingArticles, setLoadingArticles] = useState(false)
  const { findMoreReadArticles, moreReadArticles } = UseMoreReadArticles()

  const findLastThreeArticles = useCallback(async () => {
    try {
      const { data } = await api.get(`/articles?per=3`)
      setArticles(data.articles)
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingArticles(false)
    }
  }, [])

  const findHighlightsByEditorial = useCallback(async (slug: string) => {
    try {
      const { data } = await api.get(`/editorials/${slug}/highlights`)
      setHighlights(data.highlights)
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingHighlights(false)
    }
  }, [])

  const handleSelectEditorialNavBarItem = useCallback(
    (editorial: EditorialNavBarItem) => {
      setLoadingArticles(true)
      setLoadingHighlights(true)
      setSelectedEditorialNavBarItem(
        editorialNavBarItems.find(({ id }) => editorial.id === id)
      )
    },
    []
  )

  useEffect(() => {
    if (selectedEditorialNavBarItem) {
      const { slug } = selectedEditorialNavBarItem
      findLastThreeArticles()
      findHighlightsByEditorial(slug)
      findMoreReadArticles({ editorial_slug: slug })
    }
  }, [selectedEditorialNavBarItem])

  return (
    <div className={cl(className, 'flex flex-col')} {...rest}>
      <div>
        {selectedEditorialNavBarItem && !hideNavBar && (
          <EditorialNavBar
            loading={loadingArticles || loadingHighlights}
            name={selectedEditorialNavBarItem.name}
            onSelect={handleSelectEditorialNavBarItem}
            items={editorialNavBarItems}
          />
        )}
        {/* <h2 className="invisible sm:visible">Destaques</h2> */}
        <div className="grid grid-cols-12 gap-4 ">
          {first && (
            <a href={first?.path} className="col-span-12 md:col-span-6">
              <div className="flex col-span-12 md:col-span-6 flex-col md:flex-row">
                <Image
                  src={
                    first?.thumbnailImage.path || '/assets/default-article.jpg'
                  }
                  alt={first?.title || 'Imagem da notícia'}
                  width={300}
                  height={150}
                  className="object-cover rounded-lg w-full"
                />
                <div className="md:col-span-2 flex flex-col pt-2 md:pt-0 md:ps-4 relative pb-2">
                  <span className="bg-green w-fit text-white px-3 py-1 rounded text-sm font-medium mr-2 mb-2">
                    {first?.editorial?.name || 'NOTÍCIA'}
                  </span>
                  <h3 className="font-bold text-lg mb-2 h-14 overflow-hidden">
                    {first?.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {first?.shortContent || 'Conteúdo da notícia em destaque.'}
                  </p>
                  <p className="text-xs text-gray-500 mt-auto flex justify-between w-full absolute bottom-0">
                    <span>
                      {new Date(first?.publishedAt).toLocaleDateString('pt-BR')}
                    </span>
                    <span className="pe-4">
                      {first?.timeToRead} min de leitura
                    </span>
                  </p>
                </div>
              </div>
            </a>
          )}
          {second && (
            <a href={second?.path} className="col-span-12 md:col-span-6">
              <div className="flex col-span-12 md:col-span-6 flex-col md:flex-row">
                <Image
                  src={
                    second?.thumbnailImage.path || '/assets/default-article.jpg'
                  }
                  alt={second?.title || 'Imagem da notícia'}
                  width={300}
                  height={150}
                  className="object-cover rounded-lg w-full"
                />
                <div className="md:col-span-2 flex flex-col pt-2 md:pt-0 md:ps-4 relative pb-2">
                  <span className="bg-green w-fit text-white px-3 py-1 rounded text-sm font-medium mr-2 mb-2">
                    {second?.editorial?.name || 'NOTÍCIA'}
                  </span>
                  <h3 className="font-bold text-lg mb-2 h-14 overflow-hidden">
                    {second?.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {second?.shortContent || 'Conteúdo da notícia em destaque.'}
                  </p>
                  <p className="text-xs text-gray-500 mt-auto flex justify-between w-full absolute bottom-0">
                    <span>
                      {new Date(second?.publishedAt).toLocaleDateString(
                        'pt-BR'
                      )}
                    </span>
                    <span className="pe-4">
                      {second?.timeToRead} min de leitura
                    </span>
                  </p>
                </div>
              </div>
            </a>
          )}
          {third && (
            <a href={third?.path} className="col-span-12 md:col-span-6">
              <div className="flex col-span-12 md:col-span-6 flex-col md:flex-row">
                <Image
                  src={
                    third?.thumbnailImage.path || '/assets/default-article.jpg'
                  }
                  alt={third?.title || 'Imagem da notícia'}
                  width={300}
                  height={150}
                  className="object-cover rounded-lg w-full"
                />
                <div className="md:col-span-2 flex flex-col pt-2 md:pt-0 md:ps-4 relative pb-2">
                  <span className="bg-green w-fit text-white px-3 py-1 rounded text-sm font-medium mr-2 mb-2">
                    {third?.editorial?.name || 'NOTÍCIA'}
                  </span>
                  <h3 className="font-bold text-lg mb-2 h-14 overflow-hidden">
                    {third?.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {third?.shortContent || 'Conteúdo da notícia em destaque.'}
                  </p>
                  <p className="text-xs text-gray-500 mt-auto flex justify-between w-full absolute bottom-0">
                    <span>
                      {new Date(third?.publishedAt).toLocaleDateString('pt-BR')}
                    </span>
                    <span className="pe-4">
                      {third?.timeToRead} min de leitura
                    </span>
                  </p>
                </div>
              </div>
            </a>
          )}
          {fourth && (
            <a href={fourth?.path} className="col-span-12 md:col-span-6">
              <div className="flex col-span-12 md:col-span-6 flex-col md:flex-row">
                <Image
                  src={
                    fourth?.thumbnailImage.path || '/assets/default-article.jpg'
                  }
                  alt={fourth?.title || 'Imagem da notícia'}
                  width={300}
                  height={150}
                  className="object-cover rounded-lg w-full"
                />
                <div className="md:col-span-2 flex flex-col pt-2 md:pt-0 md:ps-4 relative pb-2">
                  <span className="bg-green w-fit text-white px-3 py-1 rounded text-sm font-medium mr-2 mb-2">
                    {fourth?.editorial?.name || 'NOTÍCIA'}
                  </span>
                  <h3 className="font-bold text-lg mb-2 h-14 overflow-hidden">
                    {fourth?.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {fourth?.shortContent || 'Conteúdo da notícia em destaque.'}
                  </p>
                  <p className="text-xs text-gray-500 mt-auto flex justify-between w-full absolute bottom-0">
                    <span>
                      {new Date(fourth?.publishedAt).toLocaleDateString(
                        'pt-BR'
                      )}
                    </span>
                    <span className="pe-4">
                      {fourth?.timeToRead} min de leitura
                    </span>
                  </p>
                </div>
              </div>
            </a>
          )}
        </div>
        {/* <div className="col-span-12 lg:col-span-4 flex flex-row gap-x-4">
            <Divider
              type="secondary"
              orientation="vertical"
              className="hidden lg:block"
            />
            <SidebarArticlesPreview
              className="w-full"
              articles={articles}
              articlesSecondaries={moreReadArticles}
            />
          </div> */}
      </div>
    </div>
  )
}
